import React, { useEffect, useMemo, useRef, useState } from 'react'
import { CFormInput } from '@coreui/react';
import { Card } from 'primereact/card';
import { Icon } from '@iconify/react';
import { useAuth } from '../contexts/AuthContext';
import { useProduct } from '../contexts/ProductContext';
import { useCompanySetting } from '../contexts/CompanySettingContext';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2'
import AddProduct from '../components/AddProduct';
import PageHeader from '../components/PageHeader';
import { productNav } from '../components/NavigationPath';
import ModifierSelection from '../components/ModifierSelection';
import CategorySelection from '../components/CategorySelection';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useRestaurant } from '../contexts/RestaurantContext';
import CloneItemComponent from '../components/CloneItemComponent';

const ProductList = () => {
    const { getAllProducts, getSingleProduct } = useProduct()
    const { currency } = useCompanySetting()
    const { restaurantId, toast, auth, hasPermission } = useAuth();
    const { cloneProducts } = useRestaurant()
    const isSelectAllTriggeredByUser = useRef(false);

    const canCreate = useMemo(() => hasPermission("products", "create"),  [hasPermission]);
    const canUpdate = useMemo(() => hasPermission("products", "update"), [hasPermission]);
    const canDelete = useMemo(() => hasPermission("products", "delete"), [hasPermission]);
    const canClone = useMemo(() => hasPermission("clone-product", "access"), [hasPermission]);

    const [productVisible, setProductVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editProductId, setEditProductId] = useState(null);
    const [productList, setProductList] = useState([])
    const [isLoading, setIsLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalRenderedRecords, setTotalRenderedRecords] = useState(0);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [isSelectable, setIsSelectable] = useState(false)
    const [isSelectAll, setIsSelectAll] = useState(false)
    const [propsRestaurantId, setPropsRestaurantId] = useState(null)
    const [hasMore, setHasMore] = useState(true);
    const [productDetails, setProductDetails] = useState({
        name: '',
        sku: '',
        kitchen_name: '',
        category_id: '',
        price: '',
        cost_price: '',
        modifiers: [],
        color: '#000000',
        image: '',
        refund: "",
        visible: "",
        product_type: "",
        barcode: "",
        description: "",
        enable: "",
        type: "food",
        ingredients: [],
        restaurant_id: restaurantId
    });
    const [filter, setFilter] = useState({
        limit: 50,
        filter: null,
        modifiers: null,
        categories: null,
        restaurant_id: restaurantId,
        totalRenderedRecords: 0,
    })
    let debounceTimeout = null;

    const toggleProductSelection = (productId) => {
        setSelectedProducts((prevSelected) => {
            const isSelected = prevSelected.includes(productId);
            let updatedSelected;

            if (isSelected) {
                updatedSelected = prevSelected.filter((id) => id !== productId);
            } else {
                updatedSelected = [...prevSelected, productId];
            }

            const newIsSelectAll = updatedSelected.length === totalRecords;
            isSelectAllTriggeredByUser.current = false;
            if (newIsSelectAll !== isSelectAll) {
                setIsSelectAll(newIsSelectAll);
                if (!newIsSelectAll) {
                    isSelectAllTriggeredByUser.current = true;
                }
            }

            return updatedSelected;
        });
    };

    useEffect(() => {
        if (restaurantId) {
            handleFilterChange({ ...filter, restaurant_id: restaurantId })
            setIsSelectable(false)
            setIsSelectAll(false)
            setSelectedProducts([])
            setPropsRestaurantId(null)
        }
    }, [restaurantId])

    useEffect(() => {
        if (filter) {
            fetchProducts(filter)
        }
    }, [filter])

    useEffect(() => {
        if (!isSelectable) {
            setSelectedProducts([])
            setPropsRestaurantId(null)
        }
    }, [isSelectable])

    useEffect(() => {
        if (isSelectAll) {
            fetchAllProductsForSelectAll();
        } else {
            if (!isSelectAllTriggeredByUser.current) {
                setSelectedProducts([]);
            }
            isSelectAllTriggeredByUser.current = false;
        }
    }, [isSelectAll]);

    const fetchAllProductsForSelectAll = async () => {
        try {
            if (restaurantId && isSelectAll) {
                let allFetchedProducts = []
                if (hasMore) {
                    setSelectedProducts([]);
                    allFetchedProducts = await fetchProducts({ ...filter, limit: totalRecords });
                } else {
                    allFetchedProducts = productList
                }

                if (allFetchedProducts.length > 0) {
                    const allProductIds = allFetchedProducts.map((product) => product.id);
                    const uniqueProductIds = [...new Set(allProductIds)];
                    setSelectedProducts(uniqueProductIds);
                }
            }
        } catch (error) {
            console.error("Error fetching products for Select All:", error);
        }
    };

    const handleCloneProducts = async () => {
        try {
            if (selectedProducts.length === 0) {
                toast.current?.show({ severity: 'error', summary: 'Products', detail: 'Please select at least one product to clone.', life: 3000 })
            } else if (!propsRestaurantId) {
                toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: 'Please select a destination restaurant before cloning products.', life: 3000 })
            } else {
                setIsLoading(true)
                const data = await cloneProducts(propsRestaurantId.value, selectedProducts.reverse())
                if (!data?.error) {
                    setSelectedProducts([]);
                    setIsSelectable(false)
                    setIsSelectAll(false)
                    setPropsRestaurantId(null)
                }
                setIsLoading(false)
            }
        } catch (error) {
            console.error("Error while cloning products:", error);
        }
    }

    const fetchProducts = async (filter) => {
        try {
            if (restaurantId) {
                setIsLoading(true)

                const categories_filter = filter.categories ? filter.categories.map(category => category.value) : []
                const modifiers_filter = filter.modifiers ? filter.modifiers.map(modifier => modifier.value) : []

                const data = await getAllProducts({ ...filter, categories: categories_filter, modifiers: modifiers_filter })
                if (data.products.length !== 0 && data.totalCount !== productList.length) {
                    if (isSelectAll && hasMore) {
                        setProductList(data.products)
                        setTotalRenderedRecords(data.totalCount)
                        setHasMore(false)
                    } else {
                        setProductList([...productList, ...data.products])
                        setTotalRenderedRecords(productList.length)
                    }
                    setTotalRecords(data.totalCount)
                }

                setIsLoading(false)
                return data.products;
            }
        } catch (error) {
            console.error('Error fetching product', error);
        }
    }

    const handleCardClick = async (productId) => {
        if (isSelectable) {
            toggleProductSelection(productId)
        } else {
            if(!canUpdate && !canDelete) return;
            setEditProductId(productId);
            setEditMode(true);
            setProductVisible(false);
        }
    }

    const handleFilterChange = async (newFilter) => {
        setProductList([]);
        setFilter({ ...newFilter, totalRenderedRecords: 0 });
        setHasMore(true)
    };

    const handleLoadMore = () => {
        if (productList.length === totalRecords) {
            setHasMore(false);
        } else if (productList.length === filter.limit) {
            const updatedFilter = { ...filter, totalRenderedRecords: filter.limit };

            setFilter(updatedFilter);
            setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
        } else {
            const updatedFilter = { ...filter, totalRenderedRecords: totalRenderedRecords + filter.limit };

            setFilter(updatedFilter);
            setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
        }
    };

    const fetchProductProps = async (newProductId) => {
        try {
            if (editMode && editProductId) {
                const product = await getSingleProduct(editProductId);
                const productIndex = productList.findIndex((p) => p.id === editProductId);

                if (productIndex !== -1) {
                    if (!product) {
                        const updatedProductList = [...productList];
                        updatedProductList.splice(productIndex, 1);

                        setProductList(updatedProductList);
                        setTotalRecords(totalRecords - 1);
                        setTotalRenderedRecords(totalRenderedRecords - 1)
                    } else {
                        const updatedProductList = [...productList];
                        updatedProductList[productIndex] = product;

                        setProductList(updatedProductList);
                    }
                } else {
                    setProductList([...productList, product]);
                }
            } else if (productVisible) {
                const updatedProductList = [...productList];
                const formattedPrice = parseFloat(productDetails.price).toFixed(2);

                updatedProductList.unshift({ ...productDetails, id: newProductId[0], price: formattedPrice });

                setProductList(updatedProductList);
                setTotalRecords(totalRecords + 1);
                setTotalRenderedRecords(totalRenderedRecords + 1)
            }
        } catch (error) {
            console.error('Error fetching products.', error);
        }
    };

    const renderCardsInRows = () => {
        const rows = [];
        let currentRow = [];
        let count = 0;

        productList.length > 0 && productList.forEach((product, index) => {
            currentRow.push(
                <div key={product.id} className="d-flex">
                    <Card
                        header={product.image ? <LazyLoadImage src={product.image} alt={product.name} className='product-rounded-img' /> : <Icon icon="mdi:food-outline" height={125} width={70} style={{ color: "black" }} />}
                        title={
                            <>
                                <div className='product-name' title={product.name}>{product.name}</div>
                                <div className='product-price'>{currency}{product.price}</div>
                            </>
                        }
                        className={`product-card ${selectedProducts.includes(product.id) ? "selected" : ""}`}
                        onClick={() => handleCardClick(product.id)}
                    />
                </div>
            );

            count++;
            rows.push(
                <div key={index} className="d-flex">
                    {currentRow}
                </div>
            );
            currentRow = [];
            count = 0;
        });

        return rows;
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
        }

        debounceTimeout = setTimeout(() => {
            handleFilterChange({ ...filter, filter: value });
        }, 500);
    };    

    return (
        <Layout2 items={productNav.items} navTitle={productNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title={'Products'} isProductSelect={true} />
                <div className='d-flex flex-row px-4 py-2 user-list-nav '>
                    <div className="search_box me-4">
                        <CFormInput
                            className='d-flex'
                            type="search"
                            placeholder="Keyword Search"
                            onChange={(e) => handleSearch(e)}
                        />
                    </div>
                    <div className='me-4'>
                        <ModifierSelection
                            label={null}
                            placeholder='Modifier Group'
                            value={filter.modifiers}
                            onChange={(value) => handleFilterChange({ ...filter, modifiers: value })}
                        />
                    </div>
                    <div className='me-4'>
                        <CategorySelection
                            label={null}
                            placeholder='Category'
                            value={filter.categories}
                            onChange={(value) => handleFilterChange({ ...filter, categories: value })}

                        />
                    </div>
                    {
                        canClone && 
                        <CloneItemComponent
                            propsRestaurantId={propsRestaurantId}
                            setPropsRestaurantId={setPropsRestaurantId}
                            handleCloneItems={handleCloneProducts}
                            cloneItemLabel="Clone"
                            isSelectable={isSelectable}
                            setIsSelectable={setIsSelectable}
                            isSelectAll={isSelectAll}
                            setIsSelectAll={setIsSelectAll}
                        />
                    }
                </div>
                <div className='px-4 py-2'>
                    {(productVisible || (editMode && editProductId)) && (
                        <AddProduct
                            editMode={editMode}
                            editProductId={editProductId}
                            productVisible={productVisible}
                            productDetails={productDetails}
                            setEditProductId={setEditProductId}
                            setEditMode={setEditMode}
                            setProductVisible={setProductVisible}
                            fetchProducts={fetchProductProps}
                            setProductDetails={setProductDetails}
                            canCreate={canCreate}
                            canUpdate={canUpdate}
                            canDelete={canDelete}
                        />
                    )}
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <>
                            <div className='d-flex flex-wrap'>
                                <InfiniteScroll
                                    dataLength={productList.length}
                                    next={handleLoadMore}
                                    hasMore={hasMore}
                                    className="card-container"
                                >
                                    {
                                        canCreate && 
                                        <Card
                                            title={"Add Product"}
                                            header={<Icon icon="tabler:plus" style={{ color: "#888888" }} height={70} width={70} />}
                                            className="add-product-card"
                                            onClick={() => setProductVisible(true)}
                                        />
                                    }
                                    {productList && productList.length !== 0 &&
                                        (
                                            renderCardsInRows()
                                        )}
                                </InfiniteScroll>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout2>
    );
};

export default ProductList;
